import { useCallback, useEffect, useMemo, useState } from 'react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useAssignedPlaylists,
	usePlaylistById,
	usePlaylists,
} from '@/core/hooks/usePlaylist'
import { useDebounce } from '@/core/hooks/utils/useDebounce'

interface UsePlaylistDataOptions {
	dialogOpen: boolean
	selectedPlaylists?: Playlist[]
}

export const usePlaylistData = ({
	dialogOpen,
	selectedPlaylists = [],
}: UsePlaylistDataOptions) => {
	const [currentPage, setCurrentPage] = useState(1)
	const [allPlaylists, setAllPlaylists] = useState<Playlist[]>([])
	const [hasMorePages, setHasMorePages] = useState(true)
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 500)
	const [lastDialogOpenState, setLastDialogOpenState] = useState(dialogOpen)
	const [fetchedSelectedPlaylists, setFetchedSelectedPlaylists] = useState<
		Playlist[]
	>([])

	// Determine if we're in search mode
	const isSearchMode = debouncedSearchQuery.length > 0

	// Get IDs of playlists that are stubs (need to be fetched individually)
	const stubPlaylistIds = useMemo(() => {
		return selectedPlaylists
			.filter(
				(p) =>
					p.title?.startsWith('Playlist ') && // It's a stub
					!allPlaylists.find((pl) => pl.id === p.id) && // Not in fetched results
					!fetchedSelectedPlaylists.find((pl) => pl.id === p.id) // Not already fetched
			)
			.map((p) => p.id)
	}, [selectedPlaylists, allPlaylists, fetchedSelectedPlaylists])

	// Fetch individual playlists for stubs (only first stub to avoid too many requests)
	const firstStubId = stubPlaylistIds.length > 0 ? stubPlaylistIds[0] : null
	const { data: fetchedPlaylist } = usePlaylistById({
		playlistId: firstStubId,
		config: useAccountConfig({}),
		options: {
			enabled: dialogOpen && firstStubId !== null,
		},
	})

	// Add fetched individual playlists to our collection
	useEffect(() => {
		if (
			fetchedPlaylist &&
			!fetchedSelectedPlaylists.find((p) => p.id === fetchedPlaylist.id)
		) {
			setFetchedSelectedPlaylists((prev) => [...prev, fetchedPlaylist])
		}
	}, [fetchedPlaylist, fetchedSelectedPlaylists])

	// Build params for browse mode vs search mode
	const params = useMemo(() => {
		const baseParams = {
			page: currentPage,
			per_page: 50,
			order_by: 'title',
			direction: 'asc',
		}

		if (isSearchMode) {
			return {
				...baseParams,
				query: debouncedSearchQuery,
			}
		}

		return baseParams
	}, [currentPage, debouncedSearchQuery, isSearchMode])

	// Fetch assigned playlists (custom playlists) so they can be shown first, like in /music
	const assignedParams = useMemo(() => {
		const baseParams = {
			page: 1,
			per_page: 100,
			order_by: 'title',
			direction: 'asc',
		}

		if (isSearchMode) {
			return {
				...baseParams,
				query: debouncedSearchQuery,
			}
		}

		return baseParams
	}, [debouncedSearchQuery, isSearchMode])

	// Fetch playlists based on current mode
	const {
		data: playlistsResponse,
		isLoading,
		isFetching,
		refetch,
	} = usePlaylists({
		config: useAccountConfig({
			params,
		}),
		options: {
			enabled: dialogOpen,
		},
	})

	const { data: assignedPlaylistsResponse } = useAssignedPlaylists({
		config: useAccountConfig({
			params: assignedParams,
		}),
		options: {
			enabled: dialogOpen,
		},
	})

	// Handle dialog state changes
	useEffect(() => {
		// Detect when dialog transitions from closed to open
		if (dialogOpen && !lastDialogOpenState) {
			// Dialog just opened - reset state
			setCurrentPage(1)
			setAllPlaylists([])
			setHasMorePages(true)
			setSearchQuery('')
			setFetchedSelectedPlaylists([])
			// Force a refetch when dialog opens
			refetch()
		}
		setLastDialogOpenState(dialogOpen)
	}, [dialogOpen, lastDialogOpenState, refetch])

	// Reset when search query changes (before debounce)
	useEffect(() => {
		if (searchQuery !== debouncedSearchQuery) {
			// Immediately clear results and reset page when user types
			setCurrentPage(1)
			setAllPlaylists([])
			setHasMorePages(true)
		}
	}, [searchQuery, debouncedSearchQuery])

	// Accumulate playlists when data arrives
	useEffect(() => {
		if (playlistsResponse?.data) {
			setAllPlaylists((prev) => {
				const newPlaylists =
					currentPage === 1
						? playlistsResponse.data
						: playlistsResponse.data.filter((p) => {
								const existingIds = new Set(prev.map((existing) => existing.id))
								return !existingIds.has(p.id)
							})
				const result =
					currentPage === 1 ? newPlaylists : [...prev, ...newPlaylists]
				return result
			})

			// Calculate hasMorePages from pagination metadata
			const meta = playlistsResponse.meta?.pagination || playlistsResponse.meta
			if (meta) {
				const totalPages =
					Math.ceil(meta.total / meta.per_page) || meta.total_pages
				setHasMorePages(currentPage < totalPages)
			} else {
				setHasMorePages(false)
			}
		}
	}, [playlistsResponse, currentPage])

	// Handle dialog state changes
	useEffect(() => {
		// Detect when dialog transitions from closed to open
		if (dialogOpen && !lastDialogOpenState) {
			// Dialog just opened - reset state
			setCurrentPage(1)
			setAllPlaylists([])
			setHasMorePages(true)
			setSearchQuery('')
		}
		setLastDialogOpenState(dialogOpen)
	}, [dialogOpen, lastDialogOpenState])

	const assignedPlaylists = useMemo(() => {
		const raw = assignedPlaylistsResponse?.data || []
		if (!isSearchMode) return raw
		// Defensive: if backend doesn't apply query param, filter locally
		const q = debouncedSearchQuery.trim().toLowerCase()
		if (!q) return raw
		return raw.filter((p) => (p.title || '').toLowerCase().includes(q))
	}, [assignedPlaylistsResponse?.data, isSearchMode, debouncedSearchQuery])

	const customPlaylistIds = useMemo(
		() => new Set<number>(assignedPlaylists.map((p) => p.id)),
		[assignedPlaylists]
	)

	const dedupeByIdPreserveOrder = useCallback((items: Playlist[]) => {
		const seen = new Set<number>()
		const result: Playlist[] = []
		for (const p of items) {
			if (!seen.has(p.id)) {
				seen.add(p.id)
				result.push(p)
			}
		}
		return result
	}, [])

	// Merge selected playlists at the top ONLY in browse mode
	const mergedPlaylists = useMemo(() => {
		// In search mode, show custom (assigned) first, then fetched results
		if (isSearchMode) {
			return dedupeByIdPreserveOrder([...assignedPlaylists, ...allPlaylists])
		}

		// In browse mode, merge selected playlists with fetched playlists (and custom playlists first)
		if (selectedPlaylists.length === 0) {
			return dedupeByIdPreserveOrder([...assignedPlaylists, ...allPlaylists])
		}

		// Create a map of all fetched playlists by ID (including custom + individually fetched ones)
		const fetchedMap = new Map<number, Playlist>([
			...assignedPlaylists.map((p) => [p.id, p] as const),
			...allPlaylists.map((p) => [p.id, p] as const),
		])
		fetchedSelectedPlaylists.forEach((p) => {
			if (!fetchedMap.has(p.id)) {
				fetchedMap.set(p.id, p)
			}
		})

		// For selected playlists, use the real entity if available, otherwise use the stub/passed entity
		const missingSelected = selectedPlaylists
			.map((p) => fetchedMap.get(p.id) || p)
			.filter(
				(p) =>
					!allPlaylists.find((pl) => pl.id === p.id) &&
					!assignedPlaylists.find((pl) => pl.id === p.id)
			)

		const result = [...missingSelected, ...assignedPlaylists, ...allPlaylists]

		// Return merged list: selected missing playlists first, then all fetched
		return dedupeByIdPreserveOrder(result)
	}, [
		allPlaylists,
		selectedPlaylists,
		isSearchMode,
		fetchedSelectedPlaylists,
		assignedPlaylists,
		dedupeByIdPreserveOrder,
	])

	const loadMore = useCallback(() => {
		if (hasMorePages && !isLoading) {
			setCurrentPage((prev) => prev + 1)
		}
	}, [hasMorePages, isLoading])

	const reset = useCallback(() => {
		setCurrentPage(1)
		setAllPlaylists([])
		setHasMorePages(true)
		setSearchQuery('')
	}, [])

	return {
		allPlaylists: mergedPlaylists,
		isLoading,
		isFetching,
		hasMorePages,
		loadMore,
		reset,
		searchQuery,
		setSearchQuery,
		customPlaylistIds,
	}
}
